import React, {MutableRefObject, ReactNode, useCallback, useEffect, useMemo, useRef, useState} from "react"
import {Table, TableBody} from "semantic-ui-react"
import {FormattedMessage, useIntl} from "react-intl"
import {CreatorFamHeader} from "./creator-fam-header"
import {I18nIndividual as GedcomIndividual} from "../../model/gedcom-individual"
import {I18nFamily as GedcomFamily} from "../../model/gedcom-family"
import {EventHandlers, maxRowLimit} from "../creator"
import {createRow, restoredSession, setFamilyBindings, useAutoScroll} from "../../utils/creator-utils"
import {useLocation} from "react-router"
import {CreatorFamRow} from "./creator-fam-row"
import {withContactMessage} from "../../error/contact-message"
import {CreatorTableFooter} from "../creator-footer"


export function CreatorFamTable({ data, lastId, individualsData, eventHandlers }: {
    data: GedcomFamily[]
    lastId: MutableRefObject<number>
    individualsData: GedcomIndividual[]
    eventHandlers: EventHandlers
}) {
    const location = useLocation()
    const scrollRef = useRef<HTMLDivElement>(null)
    const [errorMessage, setErrorMessage] = useState<ReactNode | null>(null)
    const placeholderInitialized = useRef(false)
    const from = location.state?.from ?? null
    const intl = useIntl()

    const rowLimitError = withContactMessage(
        {id: "creator.rows.limit"},
        intl,
        { setContactModalOpen: eventHandlers.setContactModalOpen! }
    )

    useAutoScroll(scrollRef, data.length)

    /**
     * Initialize placeholder row.
     */
    useEffect(() => {
        if (placeholderInitialized.current || from || restoredSession().restoredFamilies) return
        if (data.length === 0) eventHandlers.setFamiliesData!(prev => [createRow<GedcomFamily>(lastId, prev.length, GedcomFamily)])
        placeholderInitialized.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from])

    /**
     * If a new family is created.
     * We only want to trigger this after adding a new family record.
     * We need to observe data -data.length may not change due to the placeholder row.
     * Do NOT trigger this for data loaded from import restores 👹
     */
    useEffect(() => {
        const newRow = data.at(-1)
        if (!from && newRow && newRow.husbandId && newRow.wifeId) setFamilyBindings(newRow, individualsData)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data])

    const addRow = useCallback(() => {
        eventHandlers.setFamiliesData!(prev => {
            if (prev.length >= maxRowLimit || lastId.current >= maxRowLimit) {
                setErrorMessage(rowLimitError)
                return prev
            }
            eventHandlers.setUnsavedChanges!(true)
            return [...prev, createRow<GedcomFamily>(lastId, prev.length, GedcomFamily)]
        })
    }, [rowLimitError, lastId, eventHandlers])

    const onChange = useCallback((rowIndex: number, field: keyof GedcomFamily | keyof GedcomIndividual, value: any) => {
        eventHandlers.setFamiliesData!(prev => {
            const updated = [...prev]
            updated[rowIndex] = new GedcomFamily({ ...updated[rowIndex], [field]: value, pristine: false  })
            return updated
        })
        if (field === "enabled") eventHandlers.setUnsavedChanges!(true)
    }, [eventHandlers])

/* ------------------------------------------------------------------------------------------------------------------ */
/* MEMOIZED SUBCOMPONENTS                                                                                             */
/* ------------------------------------------------------------------------------------------------------------------ */

    const fullNames: Record<string, string> = useMemo(() =>
        Object.fromEntries(individualsData.map(indi => [indi.id, GedcomIndividual.getFullName(indi, intl.locale, false) ?? indi.id])),
    [individualsData, intl.locale])

    const childrenMap = useMemo(() => {
        const map: Record<string, GedcomIndividual[]> = {}
        individualsData.forEach(i => {
            if (i.fatherId) map[i.fatherId] ??= []
            if (i.motherId) map[i.motherId] ??= []
            i.fatherId && map[i.fatherId].push(i)
            i.motherId && map[i.motherId].push(i)
        })
        return map
    }, [individualsData])

    const spousesMap = useMemo(() => {
        const map: Record<string, GedcomFamily[]> = {}
        data.forEach(f => {
            if (f.husbandId) {
                if (!map[f.husbandId]) map[f.husbandId] = []
                map[f.husbandId].push(f)
            }
            if (f.wifeId) {
                if (!map[f.wifeId]) map[f.wifeId] = []
                map[f.wifeId].push(f)
            }
        })
        return map
    }, [data])

    const famRows = useMemo(() => data.map((fam, rowIndex) => (
        <CreatorFamRow
            key={fam.id}
            row={fam}
            rowIndex={rowIndex}
            lastId={lastId}
            data={data}
            individualsData={individualsData}
            childrenMap={childrenMap}
            spousesMap={spousesMap}
            fullNames={fullNames}
            eventHandlers={{
                switchTab: eventHandlers.switchTab,
                setIndividualsData: eventHandlers.setIndividualsData,
                setFamiliesData: eventHandlers.setFamiliesData,
                setUnsavedChanges: eventHandlers.setUnsavedChanges,
                onChange: onChange,
                setError: setErrorMessage
            }}
        />
    )), [
        data,
        individualsData,
        lastId,
        childrenMap,
        spousesMap,
        fullNames,
        onChange,
        eventHandlers.switchTab,
        eventHandlers.setIndividualsData,
        eventHandlers.setFamiliesData,
        eventHandlers.setUnsavedChanges
    ])

    const addLabel = useMemo(() => <FormattedMessage id="creator.add.family"/>, [])

/* ------------------------------------------------------------------------------------------------------------------ */
/* TABLE ROWS                                                                                                         */
/* ------------------------------------------------------------------------------------------------------------------ */

    return (
        <div className="creator creator-fam" ref={scrollRef}>
            <Table celled definition>
                {/* Header */}
                <CreatorFamHeader/>
                {/* Data */}
                <TableBody>{famRows}</TableBody>
                {/* Footer */}
                <CreatorTableFooter addLabel={addLabel} addRow={addRow} errorMessage={errorMessage}/>
            </Table>
        </div>
    )
}